<template>
  <div class="amseu-footer-bottom">
    <p class="amseu-footer-bottom__copyright">
      © {{ new Date().getFullYear() }} Arbeitsmarktservice Österreich (AMS) · Treustraße 35–43 · 1200 Wien · DVR: 0816672
    </p>
    <div class="amseu-footer-bottom__legal">
      <a
        v-for="(link, index) in legalLinks"
        :key="`legal-${index}`"
        :href="link.link"
        :target="link.modalId ? undefined : '_blank'"
        rel="noopener"
        class="amseu-footer-bottom__legal-link"
        @click="onLegalClick($event, link)"
      >{{ link.text }}</a>
    </div>

    <b-modal
      id="datenschutz-modal"
      :title="translations[3500]"
      size="xl"
      scrollable
      ok-only
      :ok-title="translations[2511]"
      hide-header-close
      :no-enforce-focus="noEnforceFocus"
      @show="loadDatenschutzText"
    >
      <div v-if="datenschutzLoading" class="text-center py-5">
        <b-spinner variant="primary" label="Wird geladen" />
      </div>
      <p v-else-if="datenschutzError" class="amseu-footer__datenschutz">
        Die Datenschutzerklärung konnte nicht geladen werden. Sie finden sie unter
        <a :href="datenschutzUrl" target="_blank" rel="noopener">{{ datenschutzUrl }}</a>.
      </p>
      <div v-else class="amseu-footer__datenschutz" v-html="datenschutzText"></div>
    </b-modal>

    <b-modal
      id="impressum-modal"
      :title="translations[3627]"
      size="lg"
      scrollable
      ok-only
      :ok-title="translations[2511]"
      hide-header-close
    >
      <div v-if="imprintText" class="amseu-footer__datenschutz" v-html="imprintText"></div>
      <p v-else class="amseu-footer__datenschutz">
        Das Impressum finden Sie unter
        <a :href="imprintUrl" target="_blank" rel="noopener">{{ imprintUrl }}</a>.
      </p>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { getPrivacyPolicyText } from '@/api/PrivacyPolicy';

interface FooterLink {
  text: string
  link: string
  modalId?: string
}

const AMS = 'https://www.ams.at';
const DATENSCHUTZ_URL = `${AMS}/organisation/ueber-ams/datenschutzbestimmungen`;
const IMPRINT_URL = `${AMS}/organisation/ueber-ams/impressum`;

@Component
export default class AmseuFooterBottom extends Vue {
  // Банер згоди має власне перехоплення фокуса: разом із фокус-трапом b-modal вони
  // зациклюються (RangeError), тож для відкриття з банера трап модалки вимикаємо.
  noEnforceFocus = false;

  datenschutzText = '';

  datenschutzLoading = false;

  datenschutzError = false;

  datenschutzUrl = DATENSCHUTZ_URL;

  imprintUrl = IMPRINT_URL;

  get translations() {
    return AppModule.translation;
  }

  // Impressum редагується в адмінці партнера (jc_imprint) і вже приходить у /jobnews/settings,
  // який App.vue завантажує при старті — окремий запит не потрібен.
  get imprintText(): string {
    return AppModule.setting?.jc_imprint || '';
  }

  legalLinks: FooterLink[] = [
    { text: 'Impressum', link: IMPRINT_URL, modalId: 'impressum-modal' },
    { text: 'Datenschutz', link: DATENSCHUTZ_URL, modalId: 'datenschutz-modal' },
    { text: 'Barrierefreiheit', link: `${AMS}/organisation/ueber-ams/barrierefreiheitserklaerung/barrierefreiheitserklaerung-ams-webseite` },
    { text: 'Sitemap', link: `${AMS}/sitemap` },
    { text: 'Nutzungsbedingungen', link: `${AMS}/organisation/ueber-ams/allgemeine-geschaeftsbedingungen` },
  ];

  onLegalClick(event: Event, link: FooterLink) {
    if (link.modalId) {
      event.preventDefault();
      this.$bvModal.show(link.modalId);
    }
  }

  // Посилання на політику всередині банера згоди веде на ams.at, але текст DSGVO
  // у нас уже є в модалці — відкриваємо її замість переходу на зовнішній сайт.
  // Слухач делегований на document: банер CookieScript вставляється динамічно
  // і перемальовується при відкритті вікна «Details anzeigen».
  onCookieBannerPrivacyClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('#cookiescript_readmore')) return;
    event.preventDefault();
    document.documentElement.classList.add('cs-datenschutz-open');
    this.noEnforceFocus = true;
    this.$bvModal.show('datenschutz-modal');
  }

  onModalHidden(_bvEvent: unknown, modalId: string) {
    if (modalId === 'datenschutz-modal') {
      document.documentElement.classList.remove('cs-datenschutz-open');
      this.noEnforceFocus = false;
    }
  }

  mounted() {
    document.addEventListener('click', this.onCookieBannerPrivacyClick, true);
    this.$root.$on('bv::modal::hidden', this.onModalHidden);
  }

  beforeDestroy() {
    document.removeEventListener('click', this.onCookieBannerPrivacyClick, true);
    this.$root.$off('bv::modal::hidden', this.onModalHidden);
  }

  // Текст DSGVO редагується в адмінці партнера, тому тягнемо його з API при відкритті модалки,
  // а не тримаємо копію у фронті. Завантажуємо один раз — футер монтується на кожній сторінці.
  async loadDatenschutzText() {
    if (this.datenschutzText || this.datenschutzLoading) return;
    this.datenschutzLoading = true;
    this.datenschutzError = false;
    try {
      const response = await getPrivacyPolicyText({
        lang: AppModule.language,
        sid: AppModule.languageSid,
        type: 'jobimpuls',
      });
      this.datenschutzText = response.text || '';
    } catch (e) {
      this.datenschutzError = true;
    } finally {
      this.datenschutzLoading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.amseu-footer-bottom {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Roboto, Arial, sans-serif;

  @media (min-width: 576px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  &__copyright {
    color: #9FBAD5;
    font-size: 12px;
    margin: 0;
  }

  &__legal {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem 1.25rem;
  }

  &__legal-link {
    color: #9FBAD5;
    font-size: 12px;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #ffffff !important;
      text-decoration: underline !important;
    }
  }
}
</style>

<style lang="scss">
.amseu-footer__datenschutz {
  font-family: Roboto, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #333333;

  strong {
    color: #00478f;
  }

  a {
    color: #0054A6;
    word-break: break-word;
  }
}
</style>
